import { AppInstallation } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthMemberAppInstallations } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { EmptyState } from '@tribeplatform/react-ui-kit/EmptyState'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { ConnectedApp } from './ConnectedApp.js'
import { ConnectedAppRowLoading } from './ConnectedAppRowLoading.js'

export const MemberConnectedApps = () => {
  const { $t } = useI18n()

  const {
    data,
    isError,
    isLoading,
    fetchNextPage,
    isFetched,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
  } = useAuthMemberAppInstallations({
    variables: {
      limit: 15,
    },
    fields: {
      nodes: {
        app: 'basic',
      },
      pageInfo: 'basic',
    },
  })

  const { nodes: connectedApps } =
    simplifyPaginatedResult<AppInstallation>(data)

  const content = connectedApps.map(appInstallation => (
    <List.Item key={appInstallation.id}>
      <ConnectedApp
        key={appInstallation.id}
        appInstallation={appInstallation}
      />
    </List.Item>
  ))

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Connected apps',
          id: 'Settings.ConnectedApps.ConnectedApps',
        })}
      />
      <Card.Content className="flex-col text-content-on-background">
        <List spacing="none" divider>
          {content}
        </List>

        {!isFetched || isLoading || isFetchingNextPage ? (
          <ConnectedAppRowLoading count={3} />
        ) : null}
        {!isLoading && isFetched && connectedApps.length === 0 && !isError && (
          <EmptyState
            description={$t({
              defaultMessage: 'You have no apps installed',
              id: 'Settings.ConnectedApps.NoAppsInstalled',
            })}
            title=""
            className="mb-2"
          />
        )}
        {hasNextPage && !isLoading && !isFetching && !isError && (
          <Button
            variant="secondaryNeutral"
            onClick={() => {
              fetchNextPage()
            }}
            className="flex-shrink-0"
            trailingIcon={
              isFetchingNextPage ? (
                <SvgIcon className="animate-spin" size="lg" name="spinner" />
              ) : null
            }
            fullWidth
          >
            <T defaultMessage="Show more" id="Generics.ShowMore" />
          </Button>
        )}
        {isError && (
          <Alert
            className="my-5 shadow"
            status="error"
            title={$t({
              defaultMessage:
                'There was an error while loading app installations',
              id: 'Settings.ConnectedApps.AppInstallationsError',
            })}
          />
        )}
      </Card.Content>
    </Card>
  )
}
